/**
 * Tooltip Component
 *
 * Displays a tooltip above an element on hover.
 * Only renders tooltip content when enabled (for conditional tooltips).
 *
 * Features:
 * - Positioned above trigger element with arrow pointing down
 * - Dark background for visibility against light cards
 * - Smooth fade-in animation (0.2s)
 * - Respects design system tokens
 */

import React, { ReactNode } from 'react';
import styles from './Tooltip.module.css';

export interface TooltipProps {
  /** Content to display in the tooltip */
  content: ReactNode;
  /** Element that triggers the tooltip on hover */
  children: ReactNode;
  /** Whether to show the tooltip (useful for conditional tooltips) */
  enabled?: boolean;
  /** Allow multiline content with word wrap */
  multiline?: boolean;
  /** Additional class name for the wrapper */
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  enabled = true,
  multiline = false,
  className = '',
}) => {
  // If tooltip is disabled, just render children without wrapper
  if (!enabled) {
    return <>{children}</>;
  }

  const tooltipClasses = [styles.tooltip, multiline ? styles.multiline : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`${styles.tooltipWrapper} ${className}`.trim()}>
      {children}
      <div className={tooltipClasses} role="tooltip" aria-hidden="true">
        {content}
      </div>
    </div>
  );
};

export default Tooltip;
